import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { airports } from "@/data/airports";
import {
  getDiningInfo,
  earlyVenues,
  lateVenues,
  terminalSlug,
} from "@/data/airportDining";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UtensilsCrossed, ExternalLink, Sunrise, Moon, MapPin } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SchemaMarkup, generateFAQSchema } from "@/components/SchemaMarkup";
import { LiveWaitStrip } from "@/components/airport/LiveWaitStrip";
import { DiningTable } from "@/components/airport/DiningTable";
import { RestaurantPreview } from "@/components/airport/RestaurantPreview";
import { AppStoreCTA } from "@/components/AppStoreCTA";
import NotFound from "./NotFound";

const AirportDining = () => {
  const { code } = useParams<{ code: string }>();
  const upper = code?.toUpperCase() ?? "";
  const lower = code?.toLowerCase() ?? "";
  const airport = airports.find((a) => a.code === upper);
  const info = getDiningInfo(upper);

  if (!airport || !info) return <NotFound />;

  const cityName = airport.city.split(",")[0];
  const url = `https://airportbuddy.app/airports/${lower}/dining`;
  const locals = info.venues.filter((v) => v.local);
  const early = earlyVenues(info);
  const late = lateVenues(info);

  const faqs = [
    {
      question: `What restaurants are in ${airport.name}?`,
      answer: `${info.summary} Notable options include ${info.venues
        .slice(0, 5)
        .map((v) => `${v.name} (${v.terminal})`)
        .join(", ")}. ${info.bestTerminal} generally has the best selection.`,
    },
    {
      question: `Is there food before security at ${upper}?`,
      answer: info.preSecurityNote,
    },
    {
      question: `What's open early at ${upper} for a morning flight?`,
      answer: `${info.earlyNote}${
        early.length ? ` Places opening by 6:00am include ${early.slice(0, 4).map((v) => v.name).join(", ")}.` : ""
      }`,
    },
    {
      question: `What's open late at ${upper}?`,
      answer: `${info.lateNote}${
        late.length ? ` Later options include ${late.slice(0, 4).map((v) => v.name).join(", ")}.` : ""
      }`,
    },
    {
      question: `Where is the best food at ${upper}?`,
      answer: `${info.bestTerminal} has the strongest selection. ${info.localPick}`,
    },
  ];

  const restaurantSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Restaurants at ${airport.name} (${upper})`,
    itemListElement: info.venues.map((v, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Restaurant",
        name: v.name,
        servesCuisine: v.cuisine,
        priceRange: v.price,
        openingHours: v.hours,
        address: {
          "@type": "PostalAddress",
          addressLocality: cityName,
          streetAddress: `${airport.name}, ${v.terminal}`,
        },
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>{`Restaurants at ${upper} Airport – Food by Terminal (${cityName})`}</title>
        <meta
          name="description"
          content={`Every place to eat at ${airport.name}: ${info.venueCount} restaurants and bars by terminal, opening hours, and what's open early. Plus live TSA wait times.`.slice(0, 158)}
        />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`Restaurants at ${upper} Airport`} />
        <meta
          property="og:description"
          content={`Food and drink at ${airport.name} by terminal, with hours and live security wait times.`}
        />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <SchemaMarkup schema={generateFAQSchema(faqs)} />
      <SchemaMarkup schema={restaurantSchema} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 pt-4 pb-12">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Airports", href: "/airports" },
              { label: upper, href: `/airports/${lower}` },
              { label: "Dining", href: `/airports/${lower}/dining` },
            ]}
          />

          <div className="max-w-3xl mx-auto space-y-6">
            <header className="space-y-3 mt-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                Restaurants at {airport.name} ({upper})
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">{info.summary}</p>
            </header>

            <Card className="rounded-xl shadow-md border-primary/20 bg-primary/5">
              <CardContent className="py-5 space-y-2">
                <p className="text-base text-foreground">
                  <strong>Best selection:</strong> {info.bestTerminal}
                </p>
                <p className="text-base text-foreground">
                  <strong>Worth going out of your way for:</strong> {info.localPick}
                </p>
              </CardContent>
            </Card>

            <LiveWaitStrip airportCode={upper} />

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-4">
                <div className="flex items-center gap-2">
                  <UtensilsCrossed className="h-5 w-5 text-primary" aria-hidden="true" />
                  <h2 className="text-xl font-bold text-foreground">
                    Every place to eat at {upper}
                  </h2>
                </div>
                <DiningTable venues={info.venues} />
                <p className="text-sm text-muted-foreground border-t pt-3">
                  Vendors, locations and hours change regularly. Confirm on the{" "}
                  <a
                    href={info.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline inline-flex items-center gap-1"
                  >
                    official {upper} dining directory
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                  .
                </p>
              </CardContent>
            </Card>

            <div className="grid gap-4 sm:grid-cols-2">
              <Card className="rounded-xl shadow-md">
                <CardContent className="py-6 space-y-2">
                  <div className="flex items-center gap-2">
                    <Sunrise className="h-5 w-5 text-primary" aria-hidden="true" />
                    <h2 className="text-lg font-bold text-foreground">Early flight?</h2>
                  </div>
                  <p className="text-base text-muted-foreground">{info.earlyNote}</p>
                  {early.length > 0 && (
                    <ul className="text-base text-foreground list-disc pl-5 space-y-1">
                      {early.slice(0, 5).map((v) => (
                        <li key={v.name}>
                          {v.name} — {v.hours}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card className="rounded-xl shadow-md">
                <CardContent className="py-6 space-y-2">
                  <div className="flex items-center gap-2">
                    <Moon className="h-5 w-5 text-primary" aria-hidden="true" />
                    <h2 className="text-lg font-bold text-foreground">Late night</h2>
                  </div>
                  <p className="text-base text-muted-foreground">{info.lateNote}</p>
                  {late.length > 0 && (
                    <ul className="text-base text-foreground list-disc pl-5 space-y-1">
                      {late.slice(0, 5).map((v) => (
                        <li key={v.name}>
                          {v.name} — {v.hours}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>

            {locals.length > 0 && (
              <Card className="rounded-xl shadow-md">
                <CardContent className="py-6 space-y-3">
                  <h2 className="text-xl font-bold text-foreground">
                    Local to {cityName}, not another chain
                  </h2>
                  <p className="text-base text-muted-foreground">
                    If you only eat one thing at {upper}, make it something you can't get at every
                    other airport.
                  </p>
                  <ul className="divide-y border-t">
                    {locals.map((v) => (
                      <li key={v.name} className="py-3">
                        <p className="text-base font-semibold text-foreground">{v.name}</p>
                        <p className="text-base text-muted-foreground">
                          {v.cuisine} · {v.terminal}
                        </p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-2">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" aria-hidden="true" />
                  <h2 className="text-xl font-bold text-foreground">
                    Eating before security at {upper}
                  </h2>
                </div>
                <p className="text-base text-muted-foreground">{info.preSecurityNote}</p>
              </CardContent>
            </Card>

            {info.terminalPages.length > 0 && (
              <Card className="rounded-xl shadow-md">
                <CardContent className="py-6 space-y-3">
                  <h2 className="text-xl font-bold text-foreground">Food by terminal</h2>
                  <div className="flex flex-wrap gap-2">
                    {info.terminalPages.map((t) => (
                      <Button
                        key={t}
                        asChild
                        variant="outline"
                        className="min-h-[44px] rounded-xl active:scale-95"
                      >
                        <Link to={`/airports/${lower}/dining/${terminalSlug(t)}`}>{t}</Link>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Live "open right now" data — secondary to the curated list, hides itself on failure. */}
            <RestaurantPreview airportCode={upper} airportName={airport.name} />

            <AppStoreCTA
              placement="dining_page"
              context={upper}
              headline={`Know how long you've got before you eat`}
              subline={`Airport Buddy watches the ${upper} security line and tells you when to leave your table. Free on iPhone.`}
            />

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-4">
                <h2 className="text-xl font-bold text-foreground">
                  Common questions about eating at {upper}
                </h2>
                <div className="space-y-4">
                  {faqs.map((f) => (
                    <div key={f.question} className="space-y-1">
                      <h3 className="text-base font-semibold text-foreground">{f.question}</h3>
                      <p className="text-base text-muted-foreground">{f.answer}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}`}>{upper} live wait times</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="min-h-[44px] rounded-xl active:scale-95 flex-1"
              >
                <Link to={`/airports/${lower}/terminals`}>{upper} terminals</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="min-h-[44px] rounded-xl active:scale-95 flex-1"
              >
                <Link to={`/airports/${lower}/parking`}>{upper} parking</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AirportDining;
